import React from "react";
import { IoIosHelpCircleOutline } from "react-icons/io";
import { FormattedMessage } from "react-intl";
import { IconWrapper } from "../utils/IconWrapper";
import { formatLink } from "../../utils/functions";

interface FormRadioProps {
  id: string;
  options?: string[];
  question?: string;
  divider?: string;
  divStyle?: string;
  inputStyle?: string;
  labelStyle?: string;
  optionLabelStyle?: string;
  dividerHeaderStyle?: string;
  repeatable?: boolean;
  containerStyle?: string;
  register?: any;
  errors?: Record<string, Object>;
  link?: string | boolean;
  cursiveLabel?: string;
  repeatableHeader?: string;
  checked: string;
  repeatableKey?: { key: string; last: boolean; amount: number };
  index?: number;
  overlayId?: string;
  overlayToggle?: (id: string) => void;
  validate?: (id: string) => void;
  changeAmount?: (key: string, direction: number) => void;
  handleInputChange: (
    event: React.ChangeEvent<HTMLElement>,
    id: string,
    type?: string
  ) => void;
}

export const FormRadio: React.FC<FormRadioProps> = ({
  id,
  options,
  handleInputChange,
  question,
  divider,
  inputStyle,
  divStyle,
  errors,
  link,
  repeatableHeader,
  repeatableKey,
  validate,
  overlayId,
  labelStyle,
  optionLabelStyle,
  dividerHeaderStyle,
  repeatable,
  containerStyle,
  register,
  cursiveLabel,
  checked,
  changeAmount,
  index,
  overlayToggle,
}) => {
  const styles = inputStyle
    ? inputStyle
    : "form-radio rounded-lg transition transform duration-500 ease-in-out hover:scale-105";

  const divStyles = divStyle
    ? divStyle
    : "flex flex-col mb-4 xl:w-11/12 xl:m-auto xl:pt-2";

  const labelStyles = labelStyle
    ? labelStyle
    : "m-2 ml-4 uppercase font-bold text-lg text-brand-dark_blue";

  const linkStyles = "hover:text-brand-dark_blue text-brand-blue mb-2";

  const optionLabelStyles = optionLabelStyle
    ? optionLabelStyle
    : "text-lg text-brand-dark_blue relative cursor-pointer select-none";

  const participantId = repeatable ? id.split("_")[1] : ""; //expected id is 'BLABLABLA_2' => 2

  const dividerHeaderStyles = dividerHeaderStyle
    ? dividerHeaderStyle
    : "m-2 uppercase font-bold text-lg text-brand-blue";

  const dividerStyles = divider ? divider : "";
  const containerStyles = containerStyle ? containerStyle : "";

  const errorDiv = errors ? (
    errors[id] ? (
      <p className='text-red-400 ml-4'>
        {" "}
        <FormattedMessage id={errors[id]["message"]} />
      </p>
    ) : null
  ) : null;

  const requiredAsterisk =
    register === null || register === undefined ? null : (
      <span className='font-normal text-red-500'>*</span>
    );

  const mouseHoverTrigger = (e: any) => {
    overlayToggle(overlayId);
  };

  const helpIcon = overlayId ? (
    <IconWrapper styles={"self-center"}>
      <IoIosHelpCircleOutline
        onMouseEnter={(e) => mouseHoverTrigger(e)}
        onMouseLeave={(e) => mouseHoverTrigger(e)}
        size={30}
      ></IoIosHelpCircleOutline>
    </IconWrapper>
  ) : null;

  const labelTag =
    typeof link === "string" ? (
      <span className={`${linkStyles}`}>
        <a href={formatLink(link)} target='_blank' rel='noreferrer'>
          {question}
          {requiredAsterisk}
        </a>
      </span>
    ) : question && overlayId ? (
      <div className='flex relative'>
        <span className={`${labelStyles}`}>
          {question}
          {requiredAsterisk}
        </span>
        {helpIcon}
      </div>
    ) : question ? (
      <span className={`${labelStyles}`}>
        {question}
        {requiredAsterisk}
      </span>
    ) : null;

  const additionalLabel = cursiveLabel ? (
    <p className='text-brand-dark_blue italic ml-4'>{cursiveLabel}</p>
  ) : null;

  const optionsDiv = "xl:flex xl:flex-wrap xl:pb-5 xl:pt-2 pl-8 xl:pl-6";

  const buttonStyle =
    "bg-blue-400 hover:bg-brand-blue text-brand-white py-2 px-4 rounded m-5 w-auto";
  const hideDecreaseRepeatableStyle = repeatableKey
    ? Number(repeatableKey.amount) === 1
      ? "invisible focus:outline-none outline-none"
      : "visible"
    : null;
  const lastOfRepeatGroup = repeatableKey
    ? repeatableKey.last
      ? true
      : false
    : false;
  const repeatableButtons = lastOfRepeatGroup ? (
    <div className='flex flex-row justify-between'>
      <button
        onClick={() => changeAmount(repeatableKey.key, 1)}
        type='button'
        className={`${buttonStyle}`}
      >
        <FormattedMessage id='Form.Repeat_add' />{" "}
        {repeatableHeader.toLowerCase()}
      </button>
      <button
        onClick={() => changeAmount(repeatableKey.key, -1)}
        type='button'
        className={`${buttonStyle} ${hideDecreaseRepeatableStyle}`}
      >
        <FormattedMessage id='Form.Repeat_remove' />{" "}
        {repeatableHeader.toLowerCase()}
      </button>
    </div>
  ) : null;

  return (
    <div className={`${divStyles} ${dividerStyles} ${containerStyles}`}>
      {divider && repeatable ? (
        <span className={`${dividerHeaderStyles}`}>
          {repeatableHeader} {participantId}
        </span>
      ) : null}
      {labelTag}
      {additionalLabel}
      <fieldset tabIndex={index}>
        <div className={optionsDiv}>
          {options?.map((option, i) => (
            <div
              key={option}
              className={`w-full xl:w-1/3 pl-4 pt-3 xl:pt-0 ${styles}`}
            >
              <label
                htmlFor={`${id}:${option}`}
                className={`${optionLabelStyles} multiSelect`}
                tabIndex={i}
              >
                <input
                  name={id}
                  value={option}
                  type='radio'
                  id={`${id}:${option}`}
                  onChange={(e) => handleInputChange(e, id)}
                  ref={register}
                  checked={checked === option}
                  onBlur={validate ? () => validate(id) : null}
                />
                <span
                  className={`absolute top-0 -left-6 h-4 w-4 rounded-full ${
                    checked === option
                      ? "bg-blue-400"
                      : "bg-blue-100 radiocircle"
                  }`}
                ></span>
                {option}
              </label>
            </div>
          ))}
        </div>
      </fieldset>
      {errorDiv}
      {repeatableButtons}
    </div>
  );
};

export default FormRadio;
